import React from "react";
import { Typography } from "@material-tailwind/react";
import { 
  DocumentTextIcon, 
  BellIcon, 
  ChatAlt2Icon, 
  CheckCircleIcon,
  UserGroupIcon,
  SearchIcon,
  HandIcon,
  CurrencyDollarIcon
} from "@heroicons/react/outline";

const HowItWorks = () => {
  const buyerSteps = [
    {
      icon: DocumentTextIcon,
      title: "Post Your RFQ",
      description: "Create a detailed request for quotation with your specific requirements, budget, and timeline.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: BellIcon,
      title: "Receive Quotes",
      description: "Get competitive quotes from verified suppliers within hours of posting your request.",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: ChatAlt2Icon,
      title: "Negotiate & Communicate",
      description: "Chat directly with suppliers to discuss details, negotiate terms, and clarify requirements.",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: CheckCircleIcon,
      title: "Choose & Complete",
      description: "Select the best supplier and complete your transaction with secure payment processing.",
      color: "bg-orange-100 text-orange-600",
    },
  ];

  const sellerSteps = [
    {
      icon: BellIcon,
      title: "Get Instant Alerts",
      description: "Receive real-time notifications when customers post requests that match your business.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: SearchIcon,
      title: "Review Opportunities",
      description: "Browse through detailed RFQs and assess which opportunities align with your capabilities.",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: DocumentTextIcon,
      title: "Submit Competitive Quotes",
      description: "Create professional quotes with pricing, delivery terms, and value propositions.",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: HandIcon,
      title: "Win Business",
      description: "Connect with customers, negotiate terms, and secure new business opportunities.",
      color: "bg-orange-100 text-orange-600",
    },
  ];

  return (
    <section id="howitworks" className="py-20 lg:py-32 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            How It Works
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            From Frustration to Success in 4 Simple Steps
          </h2>
          <Typography
            variant="p"
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Open Sans, sans-serif' }}
          >
            Tired of endless supplier calls and missed opportunities? Twerce transforms your B2B procurement 
            from a time-consuming headache into a streamlined success story.
          </Typography>
        </div>

        {/* Buyer Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
              <UserGroupIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              For Buyers: Stop Overpaying
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              No more endless supplier calls or settling for high prices. Get competitive quotes from verified suppliers in hours, not weeks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {buyerSteps.map((step, index) => (
              <div key={index} className="relative group">
                {/* Connection Line */}
                {index < buyerSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gray-200 z-0"></div>
                )}
                
                <div className="relative z-10 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-CC5500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${step.color}`}>
                    <step.icon className="w-6 h-6" />
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Seller Section */}
        <div>
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
              <CurrencyDollarIcon className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              For Suppliers: Beat Your Competitors
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Stop losing deals to faster competitors. Get instant alerts for qualified opportunities and close deals 70% faster.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sellerSteps.map((step, index) => (
              <div key={index} className="relative group">
                {/* Connection Line */}
                {index < sellerSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gray-200 z-0"></div>
                )}
                
                <div className="relative z-10 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-CC5500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${step.color}`}>
                    <step.icon className="w-6 h-6" />
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-CC5500 to-orange-600 rounded-3xl p-8 lg:p-12 text-white">
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-xl mb-8 opacity-90" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Join 5,000+ businesses already saving time and money with Twerce across Nigeria. Don't let your competitors get ahead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-CC5500 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors duration-200">
                Start Saving Today
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-CC5500 transition-all duration-200">
                See Success Stories
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
